import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import EvidencePreviewModal from '../../components/evidencePreviewModal';
import {
  downloadFinalizedResultsLogsArchive,
  downloadResultsLog,
  getFinalizedResultsLogs
} from '../../API/resultsLog';
import './style.scss';

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function formatDate(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : 'Sin fecha';
}

function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function groupAsTree(records) {
  const processes = new Map();
  records.forEach(record => {
    const processName = record?.tree?.process || 'Sin proceso';
    const mediatorName = record?.tree?.mediator || 'Sin mediador';
    const institutionName = record?.tree?.institution || 'Sin institución';
    if (!processes.has(processName)) processes.set(processName, new Map());
    const mediators = processes.get(processName);
    if (!mediators.has(mediatorName)) mediators.set(mediatorName, new Map());
    const institutions = mediators.get(mediatorName);
    if (!institutions.has(institutionName)) institutions.set(institutionName, []);
    institutions.get(institutionName).push(record);
  });
  return Array.from(processes, ([name, mediators]) => ({
    name,
    mediators: Array.from(mediators, ([mediatorName, institutions]) => ({
      name: mediatorName,
      institutions: Array.from(institutions, ([institutionName, logs]) => ({
        name: institutionName,
        logs: logs.sort((first, second) =>
          String(first.groupCode || '').localeCompare(String(second.groupCode || ''), 'es')
        )
      }))
    }))
  }));
}

export default function SuperadminResultsLogs() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let active = true;
    getFinalizedResultsLogs()
      .then(records => { if (active) setLogs(Array.isArray(records) ? records : []); })
      .catch(() => { if (active) setError('No fue posible cargar las bitácoras de cierre.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
  }, [preview]);

  const filteredLogs = useMemo(() => {
    const term = normalizeText(search);
    if (!term) return logs;
    return logs.filter(log => normalizeText([
      log.groupCode,
      log.schoolCode,
      log.schoolName,
      log.mediatorName,
      log.programScope
    ].join(' ')).includes(term));
  }, [logs, search]);
  const tree = useMemo(() => groupAsTree(filteredLogs), [filteredLogs]);

  async function loadPdf(log, mode) {
    if (busy) return;
    setBusy(`${mode}-${log.id}`);
    try {
      const pdf = await downloadResultsLog(log.groupId, log.revision);
      const groupCode = String(log.groupCode || log.groupId)
        .replace(/[^a-zA-Z0-9_-]/g, '_');
      const fileName = `BitResul_${groupCode}.pdf`;
      if (mode === 'preview') {
        if (preview?.url) URL.revokeObjectURL(preview.url);
        setPreview({ url: URL.createObjectURL(pdf), fileName });
      } else {
        saveBlob(pdf, fileName);
      }
    } catch (_) {
      toast('No fue posible obtener la bitácora en PDF.', { type: 'error' });
    } finally {
      setBusy('');
    }
  }

  async function downloadArchive() {
    if (busy || logs.length === 0) return;
    setBusy('archive');
    try {
      const archive = await downloadFinalizedResultsLogsArchive();
      saveBlob(archive, 'bitacoras_cierre_grupos_finalizados.zip');
      toast('Descarga de bitácoras iniciada.', { type: 'success' });
    } catch (_) {
      toast('No fue posible generar el archivo de bitácoras.', { type: 'error' });
    } finally {
      setBusy('');
    }
  }

  function closePreview() {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  return (
    <section className='superadmin-results-logs'>
      <EvidencePreviewModal
        open={Boolean(preview)}
        sourceUrl={preview?.url || ''}
        fileType='pdf'
        fileName={preview?.fileName || ''}
        onClose={closePreview}
        closeOnBackdrop
      />

      <div className='superadmin-results-logs__content'>
        <Link className='superadmin-results-logs__back' to='/superadmin'>
          <span className='material-symbols-rounded'>arrow_back</span>
          Volver al panel de superadmin
        </Link>

        <header className='superadmin-results-logs__heading'>
          <div>
            <p>CIERRE DE GRUPOS</p>
            <h1>Bitácoras de resultados</h1>
            <span>Grupos finalizados, organizados con la misma estructura de las evidencias.</span>
          </div>
          <button type='button' onClick={downloadArchive} disabled={Boolean(busy) || logs.length === 0}>
            <span className='material-symbols-rounded'>folder_zip</span>
            {busy === 'archive' ? 'Generando...' : 'Descargar todas'}
          </button>
        </header>

        <div className='superadmin-results-logs__toolbar'>
          <label>
            <span className='material-symbols-rounded'>search</span>
            <input
              type='search'
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder='Buscar grupo, proceso, mediador o institución'
            />
          </label>
          <strong>{filteredLogs.length} bitácoras</strong>
        </div>

        {loading && <p className='superadmin-results-logs__state'>Cargando bitácoras...</p>}
        {error && <p className='superadmin-results-logs__error' role='alert'>{error}</p>}
        {!loading && !error && filteredLogs.length === 0 && (
          <p className='superadmin-results-logs__state'>
            {search ? 'No hay bitácoras que coincidan con la búsqueda.' : 'No hay bitácoras de grupos finalizados.'}
          </p>
        )}

        <div className='superadmin-results-logs__tree'>
          {tree.map(process => (
            <details key={process.name} open>
              <summary><span className='material-symbols-rounded'>folder</span>{process.name}</summary>
              <div>
                {process.mediators.map(mediator => (
                  <details key={mediator.name} open>
                    <summary><span className='material-symbols-rounded'>person</span>{mediator.name}</summary>
                    <div>
                      {mediator.institutions.map(institution => (
                        <details key={institution.name} open>
                          <summary><span className='material-symbols-rounded'>account_balance</span>{institution.name}</summary>
                          <div className='superadmin-results-logs__groups'>
                            {institution.logs.map(log => (
                              <article key={log.id}>
                                <span className='material-symbols-rounded'>picture_as_pdf</span>
                                <div>
                                  <strong>{log.groupCode}</strong>
                                  <small>Finalizado: {formatDate(log.finalizedAt)} · Actualizada: {formatDate(log.updatedAt)}</small>
                                </div>
                                <button type='button' onClick={() => loadPdf(log, 'preview')} disabled={Boolean(busy)}>
                                  <span className='material-symbols-rounded'>visibility</span>
                                  {busy === `preview-${log.id}` ? 'Abriendo...' : 'Ver'}
                                </button>
                                <button type='button' onClick={() => loadPdf(log, 'download')} disabled={Boolean(busy)}>
                                  <span className='material-symbols-rounded'>download</span>
                                  {busy === `download-${log.id}` ? 'Descargando...' : 'PDF'}
                                </button>
                              </article>
                            ))}
                          </div>
                        </details>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
